import type { StrapiCustomerRow } from '../../api/strapiCrm';
import { customerFormDraftFromRow, type CustomerFormDraft } from './customerFormDraft';

export type CustomerFormTabKey = 'identity' | 'address' | 'office' | 'commercial';

/** `create` = form pelanggan baru. `edit` = halaman ubah. `approve` = modal sebelum pelanggan setuju. */
export type CustomerValidationMode = 'create' | 'edit' | 'approve';

type CustomerRequiredField = {
  key: keyof CustomerFormDraft;
  label: string;
  tab: CustomerFormTabKey;
  /** Nomor pelanggan diisi server saat create (`CUS-#####`). */
  skipOnCreate?: boolean;
};

/** Field form customer, urut seperti tab. Halaman ubah mewajibkan semuanya. */
const CUSTOMER_REQUIRED_FIELDS: CustomerRequiredField[] = [
  { key: 'customer_number', label: 'No. customer', tab: 'identity', skipOnCreate: true },
  { key: 'title_prefix', label: 'Gelar / awalan', tab: 'identity' },
  { key: 'name', label: 'Nama pelanggan', tab: 'identity' },
  { key: 'customer_type', label: 'Jenis pelanggan', tab: 'identity' },
  { key: 'active_status', label: 'Status', tab: 'identity' },
  { key: 'npwp', label: 'NPWP', tab: 'identity' },
  { key: 'address', label: 'Alamat', tab: 'address' },
  { key: 'alamat_lhu', label: 'Alamat LHU', tab: 'address' },
  { key: 'city', label: 'Kota', tab: 'address' },
  { key: 'province', label: 'Propinsi', tab: 'address' },
  { key: 'postal_code', label: 'Kode pos', tab: 'address' },
  { key: 'phone', label: 'No. telp', tab: 'address' },
  { key: 'mobile_wa', label: 'No. HP / WA', tab: 'address' },
  { key: 'no_telp_finance', label: 'No Telp Finance', tab: 'address' },
  { key: 'email', label: 'Email', tab: 'address' },
  { key: 'office_phone', label: 'Telp kantor', tab: 'office' },
  { key: 'fax', label: 'Fax', tab: 'office' },
  { key: 'contact_name', label: 'Nama kontak', tab: 'office' },
  { key: 'job_title', label: 'Jabatan', tab: 'office' },
  { key: 'work_email', label: 'Email kantor', tab: 'office' },
  { key: 'discount', label: 'Diskon (%)', tab: 'commercial' },
  { key: 'notes', label: 'Catatan', tab: 'commercial' },
];

/** Form buat baru: nama, jenis, PIC (nama kontak), alamat kantor, alamat LHU. */
const CUSTOMER_CREATE_KEYS = new Set<keyof CustomerFormDraft>([
  'name',
  'customer_type',
  'address',
  'alamat_lhu',
  'contact_name',
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type CustomerFieldErrorMap = Partial<Record<keyof CustomerFormDraft, string>>;

export type CustomerFormIssue = CustomerRequiredField & { message: string };

function isBlank(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'number') return !Number.isFinite(value);
  return String(value).trim() === '';
}

function discountIssue(value: unknown, required: boolean): string | null {
  if (isBlank(value)) return required ? 'Wajib diisi' : null;
  if (typeof value === 'number') return Number.isFinite(value) ? null : 'Isi angka yang valid';
  const t = String(value).trim().replace(',', '.');
  return Number.isFinite(Number(t)) ? null : 'Isi angka yang valid';
}

function emailIssue(value: unknown, required: boolean): string | null {
  if (isBlank(value)) return required ? 'Wajib diisi' : null;
  return EMAIL_RE.test(String(value).trim()) ? null : 'Format email tidak valid';
}

/** Angka NPWP saja, paling banyak 15 digit, lalu `XX.XXX.XXX.X-XXX.XXX`. */
export function formatNpwpInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 15);
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i === 2 || i === 5 || i === 8 || i === 12) out += '.';
    if (i === 9) out += '-';
    out += digits[i];
  }
  return out;
}

/**
 * NPWP 15 digit saja (tanpa NIK). Titik, tanda hubung, dan spasi diabaikan.
 * Status aktif tidak dicek ke DJP.
 */
export function npwpIssue(value: unknown, required: boolean): string | null {
  if (isBlank(value)) return required ? 'Wajib diisi' : null;
  const digits = String(value).replace(/[.\-\s]/g, '');
  if (!/^\d{15}$/.test(digits)) {
    return 'NPWP harus 15 digit dengan format 01.234.567.8-901.000.';
  }
  return null;
}

function issueMessage(
  field: CustomerRequiredField,
  value: unknown,
  required: boolean,
  mode: CustomerValidationMode,
): string | null {
  if (field.key === 'npwp' && mode === 'approve') return npwpIssue(value, required);
  if (field.key === 'discount') return discountIssue(value, required);
  if (field.key === 'email' || field.key === 'work_email') return emailIssue(value, required);
  if (!required) return null;
  return isBlank(value) ? 'Wajib diisi' : null;
}

export function customerRequiredFields(mode: CustomerValidationMode): CustomerRequiredField[] {
  if (mode === 'edit') return CUSTOMER_REQUIRED_FIELDS;
  const keys =
    mode === 'approve'
      ? new Set<keyof CustomerFormDraft>([...CUSTOMER_CREATE_KEYS, 'npwp'])
      : CUSTOMER_CREATE_KEYS;
  return CUSTOMER_REQUIRED_FIELDS.filter((field) => keys.has(field.key));
}

export function customerRequiredFieldKeySet(
  mode: CustomerValidationMode,
): ReadonlySet<keyof CustomerFormDraft> {
  return new Set(customerRequiredFields(mode).map((field) => field.key));
}

export function isCustomerFieldRequired(
  key: keyof CustomerFormDraft,
  options: { readOnly?: boolean; requiredKeys?: ReadonlySet<keyof CustomerFormDraft> },
): boolean {
  if (options.readOnly) return false;
  if (!options.requiredKeys) return true;
  return options.requiredKeys.has(key);
}

/** Label field yang masih kosong — grid pelanggan memakai mode create; quotation memakai approve. */
export function incompleteCustomerFieldLabels(
  row: StrapiCustomerRow,
  mode: 'create' | 'approve' = 'create',
): string[] {
  if (mode === 'approve') return customerApproveIssueLabels(row);
  return customerRequiredFields(mode)
    .filter((field) => isBlank(row[field.key]))
    .map((field) => field.label);
}

/**
 * Field yang menghalangi pelanggan setuju: wajib yang kosong, NPWP yang bukan 15 digit,
 * plus email atau diskon yang terisi tapi tidak valid.
 */
export function customerApproveIssueLabels(row: StrapiCustomerRow): string[] {
  return customerFormIssues(customerFormDraftFromRow(row), { mode: 'approve' }).map(
    (issue) => issue.label,
  );
}

/** Validasi form. Mode create/approve tetap menolak email atau diskon yang terisi tapi tidak valid. */
export function customerFormIssues(
  draft: CustomerFormDraft,
  options?: { isCreate?: boolean; mode?: CustomerValidationMode },
): CustomerFormIssue[] {
  const mode: CustomerValidationMode = options?.mode ?? (options?.isCreate ? 'create' : 'edit');
  const required = customerRequiredFields(mode).filter(
    (field) => !(mode === 'create' && field.skipOnCreate),
  );
  const requiredKeys = new Set(required.map((field) => field.key));
  const issues: CustomerFormIssue[] = [];
  for (const field of required) {
    const message = issueMessage(field, draft[field.key], true, mode);
    if (message) {
      issues.push({
        ...field,
        label: field.label,
        message,
      });
    }
  }
  for (const field of CUSTOMER_REQUIRED_FIELDS) {
    if (requiredKeys.has(field.key)) continue;
    if (field.key !== 'email' && field.key !== 'work_email' && field.key !== 'discount') continue;
    const message = issueMessage(field, draft[field.key], false, mode);
    if (message) issues.push({ ...field, message });
  }
  return issues;
}

export function customerFormTabLabel(
  label: string,
  tabKey: CustomerFormTabKey,
  issues: CustomerFormIssue[],
): string {
  const count = issues.filter((issue) => issue.tab === tabKey).length;
  return count > 0 ? `${label} (${count})` : label;
}
